import type { ReactNode } from 'react'

import type { ResourcePickerButton, ResourcePickerCopy } from '@krp/platform-ui'
import { Button } from '@krp/ui-kit'

/** Слова раздела над плитками и его кнопки; всё остальное в экране выбора — общее. */
export interface SourcePickerRow {
  copy: ResourcePickerCopy
  renderButton: (button: ResourcePickerButton) => ReactNode
}

/** Слова раздела: то, что у каждого раздела своё. Остальное (сбой списка, «обратитесь к администратору») у всех одинаковое. */
interface SectionWords {
  title: string
  sectionLabel: string
  loadingTitle: string
  emptyTitle: string
  noAccessTitle: string
  openLabel: string
}

const wordsOf = (words: SectionWords): ResourcePickerCopy => ({
  ...words,
  unavailableTitle: 'Не удалось узнать, какие источники вам доступны',
  emptyText: 'Подключите базу 1С или кабинет кассы в «Источниках».',
  noAccessText: 'Попросите администратора.',
})

/** Выбор источника каталога: закладка `/aqsi/catalog` и «Другой источник» в каталоге кабинета — те же плитки. */
export const CATALOG_SOURCE_PICKER_ROW: SourcePickerRow = {
  copy: wordsOf({
    title: 'Чей каталог открываем?',
    sectionLabel: 'Источники каталога',
    loadingTitle: 'Ищем источники, каталог которых вам можно смотреть…',
    emptyTitle: 'Источников каталога нет',
    noAccessTitle: 'Вам пока не выдан доступ к каталогу',
    openLabel: 'Открыть каталог',
  }),
  renderButton: (button) => (
    button.slot === 'reload' ? (
      <Button variant={button.variant} actionId="platform.catalog-source.reload" onClick={button.onClick}>{button.label}</Button>
    ) : button.slot === 'open' ? (
      <Button variant={button.variant} actionId="platform.catalog-source.open-source" onClick={button.onClick}>{button.label}</Button>
    ) : (
      <Button variant={button.variant} actionId="platform.catalog-source.sources" onClick={button.onClick}>{button.label}</Button>
    )
  ),
}

/**
 * Выбор источника у каждого раздела с `pickResource` и у окон-закладок выбора (RP-SRC1, Е-1): таблица «раздел → слова и записи
 * реестра действий». Новый раздел — новая строка здесь и три записи в `SOURCE_PICKERS` реестра действий.
 */
export const SOURCE_PICKER_ROWS: Readonly<Record<string, SourcePickerRow>> = {
  receipts: {
    copy: wordsOf({
      title: 'Чьи чеки смотрим?',
      sectionLabel: 'Источники чеков',
      loadingTitle: 'Ищем источники, чеки которых вам можно смотреть…',
      emptyTitle: 'Источников чеков нет',
      noAccessTitle: 'Вам пока не выдан доступ к чекам',
      openLabel: 'Открыть чеки',
    }),
    renderButton: (button) => (
      button.slot === 'reload' ? (
      <Button variant={button.variant} actionId="platform.receipts.reload" onClick={button.onClick}>{button.label}</Button>
    ) : button.slot === 'open' ? (
      <Button variant={button.variant} actionId="platform.receipts.open-source" onClick={button.onClick}>{button.label}</Button>
    ) : (
      <Button variant={button.variant} actionId="platform.receipts.sources" onClick={button.onClick}>{button.label}</Button>
    )
    ),
  },
  'sales-statistics': {
    copy: wordsOf({
      title: 'Чью статистику продаж смотрим?',
      sectionLabel: 'Источники статистики',
      loadingTitle: 'Ищем источники, статистику которых вам можно смотреть…',
      emptyTitle: 'Источников статистики нет',
      noAccessTitle: 'Вам пока не выдан доступ к статистике продаж',
      openLabel: 'Открыть статистику',
    }),
    renderButton: (button) => (
      button.slot === 'reload' ? (
      <Button variant={button.variant} actionId="platform.sales-statistics.reload" onClick={button.onClick}>{button.label}</Button>
    ) : button.slot === 'open' ? (
      <Button variant={button.variant} actionId="platform.sales-statistics.open-source" onClick={button.onClick}>{button.label}</Button>
    ) : (
      <Button variant={button.variant} actionId="platform.sales-statistics.sources" onClick={button.onClick}>{button.label}</Button>
    )
    ),
  },
  shifts: {
    copy: wordsOf({
      title: 'Чьи смены смотрим?',
      sectionLabel: 'Источники смен',
      loadingTitle: 'Ищем источники, смены которых вам можно смотреть…',
      emptyTitle: 'Источников смен нет',
      noAccessTitle: 'Вам пока не выдан доступ к сменам',
      openLabel: 'Открыть смены',
    }),
    renderButton: (button) => (
      button.slot === 'reload' ? (
      <Button variant={button.variant} actionId="platform.shifts.reload" onClick={button.onClick}>{button.label}</Button>
    ) : button.slot === 'open' ? (
      <Button variant={button.variant} actionId="platform.shifts.open-source" onClick={button.onClick}>{button.label}</Button>
    ) : (
      <Button variant={button.variant} actionId="platform.shifts.sources" onClick={button.onClick}>{button.label}</Button>
    )
    ),
  },
  prices: {
    copy: wordsOf({
      title: 'Чьими ценами управляем?',
      sectionLabel: 'Источники цен',
      loadingTitle: 'Ищем источники, ценами которых вам можно управлять…',
      emptyTitle: 'Источников цен нет',
      noAccessTitle: 'Вам пока не выдан доступ к ценам',
      openLabel: 'Открыть цены',
    }),
    renderButton: (button) => (
      button.slot === 'reload' ? (
      <Button variant={button.variant} actionId="platform.prices.reload" onClick={button.onClick}>{button.label}</Button>
    ) : button.slot === 'open' ? (
      <Button variant={button.variant} actionId="platform.prices.open-source" onClick={button.onClick}>{button.label}</Button>
    ) : (
      <Button variant={button.variant} actionId="platform.prices.sources" onClick={button.onClick}>{button.label}</Button>
    )
    ),
  },
  supplies: {
    copy: wordsOf({
      title: 'С чьими поставками работаем?',
      sectionLabel: 'Источники поставок',
      loadingTitle: 'Ищем источники, поставками которых вам можно заниматься…',
      emptyTitle: 'Источников поставок нет',
      noAccessTitle: 'Вам пока не выдан доступ к поставкам',
      openLabel: 'Открыть поставки',
    }),
    renderButton: (button) => (
      button.slot === 'reload' ? (
      <Button variant={button.variant} actionId="platform.supplies.reload" onClick={button.onClick}>{button.label}</Button>
    ) : button.slot === 'open' ? (
      <Button variant={button.variant} actionId="platform.supplies.open-source" onClick={button.onClick}>{button.label}</Button>
    ) : (
      <Button variant={button.variant} actionId="platform.supplies.sources" onClick={button.onClick}>{button.label}</Button>
    )
    ),
  },
  inventory: {
    copy: wordsOf({
      title: 'Чью инвентаризацию ведём?',
      sectionLabel: 'Источники инвентаризации',
      loadingTitle: 'Ищем источники, инвентаризацию которых вам можно вести…',
      emptyTitle: 'Источников инвентаризации нет',
      noAccessTitle: 'Вам пока не выдан доступ к инвентаризации',
      openLabel: 'Открыть инвентаризацию',
    }),
    renderButton: (button) => (
      button.slot === 'reload' ? (
      <Button variant={button.variant} actionId="platform.inventory.reload" onClick={button.onClick}>{button.label}</Button>
    ) : button.slot === 'open' ? (
      <Button variant={button.variant} actionId="platform.inventory.open-source" onClick={button.onClick}>{button.label}</Button>
    ) : (
      <Button variant={button.variant} actionId="platform.inventory.sources" onClick={button.onClick}>{button.label}</Button>
    )
    ),
  },
  'price-tags': {
    copy: wordsOf({
      title: 'Чьи ценники печатаем?',
      sectionLabel: 'Источники ценников',
      loadingTitle: 'Ищем источники, ценники которых вам можно печатать…',
      emptyTitle: 'Источников ценников нет',
      noAccessTitle: 'Вам пока не выдан доступ к ценникам',
      openLabel: 'Открыть ценники',
    }),
    renderButton: (button) => (
      button.slot === 'reload' ? (
      <Button variant={button.variant} actionId="platform.price-tags.reload" onClick={button.onClick}>{button.label}</Button>
    ) : button.slot === 'open' ? (
      <Button variant={button.variant} actionId="platform.price-tags.open-source" onClick={button.onClick}>{button.label}</Button>
    ) : (
      <Button variant={button.variant} actionId="platform.price-tags.sources" onClick={button.onClick}>{button.label}</Button>
    )
    ),
  },
  import: {
    copy: wordsOf({
      title: 'В какую базу загружаем накладные?',
      sectionLabel: 'Источники для импорта накладных',
      loadingTitle: 'Ищем базы, куда вам можно загружать накладные…',
      emptyTitle: 'Баз для импорта накладных нет',
      noAccessTitle: 'Вам пока не выдан доступ к импорту накладных',
      openLabel: 'Открыть импорт',
    }),
    renderButton: (button) => (
      button.slot === 'reload' ? (
      <Button variant={button.variant} actionId="platform.import.reload" onClick={button.onClick}>{button.label}</Button>
    ) : button.slot === 'open' ? (
      <Button variant={button.variant} actionId="platform.import.open-source" onClick={button.onClick}>{button.label}</Button>
    ) : (
      <Button variant={button.variant} actionId="platform.import.sources" onClick={button.onClick}>{button.label}</Button>
    )
    ),
  },
  structure: {
    copy: wordsOf({
      title: 'Чью структуру смотрим?',
      sectionLabel: 'Источники структуры',
      loadingTitle: 'Ищем источники, структуру которых вам можно смотреть…',
      emptyTitle: 'Источников структуры нет',
      noAccessTitle: 'Вам пока не выдан доступ к структуре',
      openLabel: 'Открыть структуру',
    }),
    renderButton: (button) => (
      button.slot === 'reload' ? (
      <Button variant={button.variant} actionId="platform.structure.reload" onClick={button.onClick}>{button.label}</Button>
    ) : button.slot === 'open' ? (
      <Button variant={button.variant} actionId="platform.structure.open-source" onClick={button.onClick}>{button.label}</Button>
    ) : (
      <Button variant={button.variant} actionId="platform.structure.sources" onClick={button.onClick}>{button.label}</Button>
    )
    ),
  },
  duplicates: {
    copy: wordsOf({
      title: 'В каком источнике ищем дубли?',
      sectionLabel: 'Источники для поиска дублей',
      loadingTitle: 'Ищем источники, в которых вам можно искать дубли…',
      emptyTitle: 'Источников для поиска дублей нет',
      noAccessTitle: 'Вам пока не выдан доступ к товарам источников',
      openLabel: 'Выбрать источник',
    }),
    renderButton: (button) => (
      button.slot === 'reload' ? (
      <Button variant={button.variant} actionId="platform.duplicates-source.reload" onClick={button.onClick}>{button.label}</Button>
    ) : button.slot === 'open' ? (
      <Button variant={button.variant} actionId="platform.duplicates-source.open-source" onClick={button.onClick}>{button.label}</Button>
    ) : (
      <Button variant={button.variant} actionId="platform.duplicates-source.sources" onClick={button.onClick}>{button.label}</Button>
    )
    ),
  },
  'catalog-source': CATALOG_SOURCE_PICKER_ROW,
}
